import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import { Field, selectBox } from "trader.ts/ui.forms";
import * as style from "../../styles/style.css.classes";
import * as FA from "../../styles/fontawesome.css.classes";
import { range } from "../lib/range";
import { dateInDaterange, getFirstWeekdayFrom, getMonday } from "./../lib/time";
import { translateMonth } from "./../translations_specific";
import { dyn } from "trader.ts/ui";
import { isNil } from "../lib/istype";

export async function weekField(opts: {
  initialVal: joda.LocalDate;
  firstday: joda.LocalDate;
  lastday: joda.LocalDate;
  label: string;
  isDisabled?: (w: {
    index: number;
    firstday: joda.LocalDate;
    lastday: joda.LocalDate;
  }) => boolean;
  saveAndLoadInitialValToLocalStorage?: string;
}): Promise<
  Field<{ index: number; firstday: joda.LocalDate; lastday: joda.LocalDate }>
> {
  const options = makeWeeksFromPeriod(opts);
  const init = options.find((week) =>
    dateInDaterange(
      { date: opts.initialVal },
      { from: week.firstday, until: week.lastday },
    ),
  );

  const renderDate = (d: joda.LocalDate) =>
    d.dayOfMonth() + " " + translateMonth(d.month()).slice(0, 3).toLowerCase();

  const isDisabled = opts.isDisabled || (() => false);

  const select = await selectBox({
    values: options,
    initial: init,
    show: (w) =>
      "Week" +
      " " +
      (w.index + 1).toString() +
      " (" +
      renderDate(w.firstday) +
      " - " +
      renderDate(w.lastday) +
      ")",
    label: opts.label,
    isDisabled,
    saveAndLoadInitialValToLocalStorage:
      opts.saveAndLoadInitialValToLocalStorage,
  });
  return {
    ...select,
    render: () => {
      return (
        <div>
          {select.render()}
          {dyn(select.s, (week) => {
            if (week.tag === "parsed") {
              const i = options.findIndex(
                (opt) => opt.index === week.parsed.index,
              );
              const nextWeek = options[i - 1];
              const prevWeek = options[i + 1];
              const render = (
                w: (typeof options)[number] | null | undefined,
                dir: "left" | "right",
              ) => (
                <button
                  className={
                    style.button_small +
                    style.text_with_icon +
                    (dir === "right" ? style.icon_on_right : "")
                  }
                  disabled={isNil(w) ? "disabled" : undefined}
                  style="flex: 1;"
                  onclick={() =>
                    w ? select.s.set({ tag: "parsed", parsed: w }) : {}
                  }
                >
                  {dir === "left" ? <i className={FA.icon_arrow_left}></i> : []}
                  <div>
                    {w ? "Week" + " " + (w.index + 1).toString() : ""}
                  </div>
                  {dir === "right" ? (
                    <i className={FA.icon_arrow_right}></i>
                  ) : (
                    []
                  )}
                </button>
              );
              return (
                <div style="margin-top: -8px; margin-bottom: 16px; display: flex;">
                  {render(isDisabled(nextWeek) ? null : nextWeek, "left")}
                  {render(isDisabled(prevWeek) ? null : prevWeek, "right")}
                </div>
              );
            } else {
              return [];
            }
          })}
        </div>
      );
    },
  };
}

export function makeWeeksFromPeriod(opts: {
  firstday: joda.LocalDate;
  lastday: joda.LocalDate;
}): { index: number; firstday: joda.LocalDate; lastday: joda.LocalDate }[] {
  const amountOfWeeksInPeriod = Math.ceil(
    (getFirstWeekdayFrom(opts.lastday, joda.DayOfWeek.SUNDAY).toEpochDay() -
      getMonday(opts.firstday).toEpochDay()) /
      7,
  );

  return range(0, amountOfWeeksInPeriod).map((index) => {
    const d = opts.firstday.plusDays(index * 7);
    const d1 = getMonday(d);
    const d2 = getFirstWeekdayFrom(d, joda.DayOfWeek.SUNDAY);
    return {
      index,
      firstday: d1,
      lastday: d2,
    };
  });
}
